// @LTG-ID: F-010
// @STATUS: BUILT
// @HERMETIC: false  // consumes SectorContext, ExternalCTA, INDUSTRY_MULTIPLES
// @DOCS: 08_FEATURES.md#F-010, 10_DECISIONS.md#ADR-012 (D1, D2)
// @CHANGED: 2026-02 session_003 (CHG-0039)
// @OWNER: web
//
// F-010 — Strategic-Snapshot valuation calculator. Ported verbatim from
// /app/source_reference/Landing Page.html lines 2344-2460.
// Hero `#snapshot` anchor + PageNav "Calculator" tab both target id="snapshot".
//
// Deep-link contract: when the visitor lands with `?sector=<key>`, the industry
// dropdown auto-selects whatever `sectors.js → industryMultipleKey` maps that
// sector to. If no sector is selected, defaults to "construction" per source.
//
// Math (source line 2358-2365, ported verbatim):
//   ebitda    = revenue × margin/100
//   base      = ind.basis === "Revenue" ? revenue : ebitda
//   growthAdj = 1 + (growth - 8) × 0.012
//   low       = base × ind.low  × growthAdj
//   high      = base × ind.high × growthAdj
//
// ADR-012 D2 clamp (defensive only): `low = Math.max(0, lowRaw); high = Math.max(low, highRaw)`.
// Within slider ranges (revenue ∈ [500K, 50M], margin ∈ [5, 45], growth ∈ [-10, 45]),
// growthAdj ∈ [0.784, 1.444] is always positive, so low > 0 is mathematically guaranteed.
// The clamp exists to defend against URL-tampered inputs.

import { useEffect, useState } from "react";
import { INDUSTRY_MULTIPLES } from "../../data/industryMultiples";
import { getSector } from "../../data/sectors";
import { useSector } from "../../lib/use-sector";
import ExternalCTA from "../../lib/external-cta";
import "./snapshot.css";

// fmtUSD — verbatim from source `Landing Page.html` line 1500.
// Produces compact M/K abbreviations for use inside the indicative-range output
// (where a 64px headline cannot fit a long `$1,234,567` string) and the
// method-card. This is the source's behavior; we ship it pixel-faithful per
// the hard rule. (Note: the orchestrator's plan-back answer said "comma
// separators" but that was a plan-back-vs-source misalignment; the source uses
// M/K. Flagged in the session_003 reply to the orchestrator.)
function fmtUSD(n) {
    if (n >= 1_000_000) {
        return (n / 1_000_000).toFixed(1).replace(/\.0$/, "") + "M";
    }
    if (n >= 1_000) return Math.round(n / 1_000) + "K";
    return Math.round(n).toString();
}

export function Snapshot() {
    const { sector } = useSector();
    const [revenue, setRevenue] = useState(7400000);
    const [margin, setMargin] = useState(22);
    const [industry, setIndustry] = useState("construction");
    const [growth, setGrowth] = useState(12);

    // Deep-link the industry off the currently-selected sector.
    useEffect(() => {
        if (!sector) return;
        const s = getSector(sector);
        if (s && INDUSTRY_MULTIPLES[s.industryMultipleKey]) {
            setIndustry(s.industryMultipleKey);
        }
    }, [sector]);

    const ind = INDUSTRY_MULTIPLES[industry];
    const ebitda = revenue * (margin / 100);
    const base = ind.basis === "Revenue" ? revenue : ebitda;
    const growthAdj = 1 + (growth - 8) * 0.012;
    const lowRaw = base * ind.low * growthAdj;
    const highRaw = base * ind.high * growthAdj;
    const low = Math.max(0, lowRaw);
    const high = Math.max(low, highRaw);

    return (
        <section
            className="ltg-snapshot-section"
            id="snapshot"
            data-testid="snapshot-section"
        >
            <div className="ltg-container">
                <div className="ltg-block-head">
                    <span className="ltg-eyebrow" data-testid="snapshot-eyebrow">
                        Six-minute snapshot
                    </span>
                    <h2 data-testid="snapshot-h2">
                        Start with a <em>real number,</em> not a curiosity gap.
                    </h2>
                </div>

                <div className="ltg-snapshot" data-testid="snapshot-card">
                    <div className="ltg-snapshot__form">
                        <span
                            className="ltg-eyebrow ltg-snapshot__eyebrow-inputs"
                            aria-hidden="true"
                        >
                            Inputs
                        </span>
                        <h3 className="ltg-snapshot__h3">
                            Tell us about the business. We'll show our work.
                        </h3>

                        <div className="ltg-snapshot__field">
                            <label htmlFor="snapshot-industry">Industry</label>
                            <select
                                id="snapshot-industry"
                                className="ltg-snapshot__input"
                                value={industry}
                                onChange={(e) => setIndustry(e.target.value)}
                                data-testid="snapshot-industry"
                            >
                                {Object.entries(INDUSTRY_MULTIPLES).map(
                                    ([k, v]) => (
                                        <option key={k} value={k}>
                                            {v.label}
                                        </option>
                                    )
                                )}
                            </select>
                        </div>

                        <div className="ltg-snapshot__field">
                            <label htmlFor="snapshot-revenue">
                                Annual revenue (trailing 12 months)
                            </label>
                            <input
                                id="snapshot-revenue"
                                className="ltg-snapshot__input ltg-snapshot__range"
                                type="range"
                                min={500000}
                                max={50000000}
                                step={100000}
                                value={revenue}
                                onChange={(e) => setRevenue(+e.target.value)}
                                aria-valuetext={`$${(revenue / 1_000_000).toFixed(revenue < 5_000_000 ? 2 : 1)}M`}
                                data-testid="snapshot-revenue"
                            />
                            <div
                                className="ltg-snapshot__field-value"
                                data-testid="snapshot-revenue-display"
                            >
                                $
                                {(revenue / 1_000_000).toFixed(
                                    revenue < 5_000_000 ? 2 : 1
                                )}
                                M
                            </div>
                        </div>

                        <div className="ltg-snapshot__field-row">
                            <div className="ltg-snapshot__field">
                                <label htmlFor="snapshot-margin">
                                    EBITDA margin
                                </label>
                                <input
                                    id="snapshot-margin"
                                    className="ltg-snapshot__input ltg-snapshot__range"
                                    type="range"
                                    min={5}
                                    max={45}
                                    step={1}
                                    value={margin}
                                    onChange={(e) =>
                                        setMargin(+e.target.value)
                                    }
                                    data-testid="snapshot-margin"
                                />
                                <div
                                    className="ltg-snapshot__field-value"
                                    data-testid="snapshot-margin-display"
                                >
                                    {margin}%
                                </div>
                            </div>
                            <div className="ltg-snapshot__field">
                                <label htmlFor="snapshot-growth">
                                    3-yr growth
                                </label>
                                <input
                                    id="snapshot-growth"
                                    className="ltg-snapshot__input ltg-snapshot__range"
                                    type="range"
                                    min={-10}
                                    max={45}
                                    step={1}
                                    value={growth}
                                    onChange={(e) =>
                                        setGrowth(+e.target.value)
                                    }
                                    data-testid="snapshot-growth"
                                />
                                <div
                                    className="ltg-snapshot__field-value"
                                    data-testid="snapshot-growth-display"
                                >
                                    {growth > 0 ? "+" : ""}
                                    {growth}%
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="ltg-snapshot__out">
                        <span className="ltg-snapshot__out-label">
                            Indicative valuation range
                        </span>
                        <div
                            className="ltg-snapshot__range-out"
                            data-testid="snapshot-range-out"
                        >
                            <span className="ltg-snapshot__currency">$</span>
                            {fmtUSD(low)}
                            <small> to </small>${fmtUSD(high)}
                        </div>
                        <p className="ltg-snapshot__out-sub">
                            Indicative preview. Your full Strategic Snapshot
                            considers approximately forty more variables and is
                            produced under the full KGOB methodology.
                        </p>

                        <div
                            className="ltg-snapshot__method-card"
                            data-testid="snapshot-method-card"
                        >
                            <div className="ltg-snapshot__method-row">
                                <span className="ltg-snapshot__method-k">
                                    Method
                                </span>
                                <span className="ltg-snapshot__method-v">
                                    {ind.basis === "Revenue"
                                        ? "Revenue multiple"
                                        : "EBITDA multiple"}
                                </span>
                            </div>
                            <div className="ltg-snapshot__method-row">
                                <span className="ltg-snapshot__method-k">
                                    Base ({ind.basis.toLowerCase()})
                                </span>
                                <span className="ltg-snapshot__method-v">
                                    ${fmtUSD(base)}
                                </span>
                            </div>
                            <div className="ltg-snapshot__method-row">
                                <span className="ltg-snapshot__method-k">
                                    Multiple range
                                </span>
                                <span className="ltg-snapshot__method-v">
                                    {ind.low.toFixed(1)}× –{" "}
                                    {ind.high.toFixed(1)}×
                                </span>
                            </div>
                            <div className="ltg-snapshot__method-row">
                                <span className="ltg-snapshot__method-k">
                                    Growth adjustment
                                </span>
                                <span className="ltg-snapshot__method-v">
                                    {growthAdj > 1 ? "+" : ""}
                                    {((growthAdj - 1) * 100).toFixed(1)}%
                                </span>
                            </div>
                            <div className="ltg-snapshot__method-row">
                                <span className="ltg-snapshot__method-k">
                                    Primary lever
                                </span>
                                <span className="ltg-snapshot__method-v">
                                    {ind.driver}
                                </span>
                            </div>
                        </div>

                        <div className="ltg-snapshot__cta-box">
                            <div className="ltg-snapshot__cta-line">
                                Like what you see? Get your full Snapshot:
                                methodology, peer benchmarks, and three priority
                                moves.
                            </div>
                            <ExternalCTA
                                ctaId="snapshot-order"
                                className="ltg-btn ltg-btn-teal ltg-snapshot__cta-btn"
                                data-testid="snapshot-order-cta"
                            >
                                Get Your Valuation{" "}
                                <span
                                    className="ltg-arrow"
                                    aria-hidden="true"
                                >
                                    →
                                </span>
                            </ExternalCTA>
                            <span className="ltg-snapshot__disclaimer">
                                Indicative valuation. Not a formal appraisal
                                under SSVS No. 1.
                            </span>
                        </div>
                        <p className="ltg-snapshot__method-note">
                            Multiples sourced from Q1 2026 GF Data, Pepperdine
                            PCMS, and BVR DealStats. Updated quarterly.
                        </p>
                    </div>
                </div>
            </div>
        </section>
    );
}

export default Snapshot;
